import { ColumnDef } from "@tanstack/react-table";
import { EditIcon, ReplaceIcon, TrashAltIcon } from "assets/icons";
import { useLanguage } from "hooks";
import { t } from "i18next";
import moment from "moment";
/* eslint-disable react-hooks/exhaustive-deps */
import { useMemo, useState } from "react";
import { ItemsTable, PersonInfo, TableItemActionsControl } from "ui";

import { StaffManagerListItem } from "../../staff.types";
import { StaffManagersTableProps } from "./StaffManagersTable.types";

const StaffManagersTable = ({
  managers,
  sorting,
  setSorting,
  onChangeManagerDeleteStatus,
  onEditManager,
  isLoading
}: StaffManagersTableProps) => {
  const { currentLanguage } = useLanguage();

  const [openedItemOptionsIndex, setOpenedItemOptionsIndex] = useState<
    number | null
  >(null);

  const columns: Array<
    ColumnDef<StaffManagerListItem, StaffManagerListItem[]>
  > = useMemo(
    () => [
      {
        id: "name",
        accessorKey: "name",
        header: t("User"),
        size: 48,
        minSize: 48,
        cell: ({ row }) => (
          <PersonInfo
            image={managers[row.index].image}
            personName={`${managers[row.index].firstName} ${
              managers[row.index].lastName
            }`}
          />
        )
      },
      {
        id: "email",
        accessorKey: "email",
        header: t("Email"),
        size: 30,
        minSize: 30,
        cell: ({ row }) => managers[row.index].email
      },
      {
        header: t("Role"),
        size: 30,
        minSize: 30,
        cell: ({ row }) => t(managers[row.index].role)
      },
      {
        accessorKey: "dotsColumn",
        header: () => null,
        size: 4,
        minSize: 4,
        cell: ({ row }) => {
          const daysLeftToRestore = Math.ceil(
            moment(row.original.accountRestorationDeadline).diff(
              moment(),
              "days",
              true
            )
          );

          if (row.original.isDeleted && daysLeftToRestore <= 0) {
            return null;
          }

          return (
            <TableItemActionsControl
              actionsList={[
                ...(row.original.isDeleted
                  ? []
                  : [
                      {
                        label: "Edit",
                        Icon: (
                          <EditIcon fill={"#090914"} width={18} height={18} />
                        ),
                        onClick: () => onEditManager(managers[row.index])
                      }
                    ]),
                {
                  label: row.original.isDeleted ? t("Restore") : t("Delete"),
                  Icon: row.original.isDeleted ? (
                    <ReplaceIcon fill={"#090914"} width={17} height={17} />
                  ) : (
                    <TrashAltIcon fill={"#ff0033"} width={18} height={18} />
                  ),
                  labelColor: row.original.isDeleted ? "#090914" : "#ff0033",
                  onClick: () => onChangeManagerDeleteStatus(row.original)
                }
              ]}
              rowIndex={row.index}
              setOpenedItemOptionsIndex={setOpenedItemOptionsIndex}
              openedItemOptionsIndex={openedItemOptionsIndex}
            />
          );
        },
        enableSorting: false
      }
    ],
    [managers, openedItemOptionsIndex, currentLanguage]
  );

  return (
    <ItemsTable
      columns={columns}
      data={managers}
      sorting={sorting}
      skeletonHeight={65}
      setSorting={setSorting}
      isLoading={isLoading}
    />
  );
};

export default StaffManagersTable;
